import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Bomb, CloudRain, Copy, Flame, Flower2, Heart, Moon, PartyPopper, Plus, Radio, Rocket, Shuffle,
  Snowflake, Sparkles, Star, Trash2, Wand2, X, Zap, Cloud, CircleDot,
} from "lucide-react";
import { useDesign } from "../state/designStore";
import { EFFECT_LABELS, MAX_EFFECT_ITEMS } from "../types";
import type { EffectType } from "../types";
import { SliderControl } from "./SliderControl";
import { ColorSwatchButton } from "./ColorSheet";
import { BottomSheet } from "./BottomSheet";

type Cat = "celebrate" | "nature" | "energy";

interface Meta {
  Icon: LucideIcon;
  cat: Cat;
  hint: string;
  grad: [string, string];
  anim: "spin" | "float" | "fall" | "pulse";
  /** تقريباً كم كيلوبايت (مضغوط) يضيف للملصق بالإعدادات الافتراضية */
  weight: number;
}

export const EFFECT_META: Record<EffectType, Meta> = {
  fireworks: { Icon: Rocket, cat: "celebrate", hint: "انفجارات ملوّنة بالسماء", grad: ["#FF8A3D", "#E5484D"], anim: "pulse", weight: 5.6 },
  confetti: { Icon: PartyPopper, cat: "celebrate", hint: "قصاصات ملوّنة تتساقط", grad: ["#34D399", "#3B82F6"], anim: "fall", weight: 7.9 },
  sparkles: { Icon: Wand2, cat: "celebrate", hint: "بريق ولمعان ناعم", grad: ["#FBBF24", "#F59E0B"], anim: "pulse", weight: 6.5 },
  stars: { Icon: Star, cat: "celebrate", hint: "حلقات نجوم تدور", grad: ["#FACC15", "#F97316"], anim: "spin", weight: 2.5 },
  hearts: { Icon: Heart, cat: "celebrate", hint: "قلوب تطلع وتتمايل", grad: ["#FB7185", "#E11D48"], anim: "float", weight: 6.8 },
  flowers: { Icon: Flower2, cat: "nature", hint: "ورود تتساقط بنعومة", grad: ["#F472B6", "#C026D3"], anim: "fall", weight: 5.6 },
  snow: { Icon: Snowflake, cat: "nature", hint: "ثلج هادئ", grad: ["#7DD3FC", "#6366F1"], anim: "fall", weight: 4.1 },
  rain: { Icon: CloudRain, cat: "nature", hint: "مطر مع رذاذ", grad: ["#60A5FA", "#1D4ED8"], anim: "fall", weight: 8.4 },
  bubbles: { Icon: CircleDot, cat: "nature", hint: "فقاعات تطفو", grad: ["#67E8F9", "#3B82F6"], anim: "float", weight: 4.3 },
  fireflies: { Icon: Moon, cat: "nature", hint: "يراعات متوهجة", grad: ["#BEF264", "#16A34A"], anim: "float", weight: 2.8 },
  fire: { Icon: Flame, cat: "energy", hint: "ألسنة نار وجمر", grad: ["#FB923C", "#DC2626"], anim: "pulse", weight: 9.0 },
  explosion: { Icon: Bomb, cat: "energy", hint: "انفجار بموجات صدمة", grad: ["#F97316", "#B91C1C"], anim: "pulse", weight: 5.0 },
  lightning: { Icon: Zap, cat: "energy", hint: "برق يومض", grad: ["#A5B4FC", "#4F46E5"], anim: "pulse", weight: 2.3 },
  ripple: { Icon: Radio, cat: "energy", hint: "موجات تنتشر", grad: ["#22D3EE", "#0284C7"], anim: "spin", weight: 0.9 },
  smoke: { Icon: Cloud, cat: "energy", hint: "دخان ناعم يتصاعد", grad: ["#94A3B8", "#475569"], anim: "float", weight: 3.0 },
};

const CATS: { id: Cat; title: string }[] = [
  { id: "celebrate", title: "احتفال" },
  { id: "nature", title: "طبيعة" },
  { id: "energy", title: "طاقة وقوة" },
];

const PALETTE = ["#FFD700", "#FF7A00", "#FF3B5C", "#FF6FAE", "#B26BFF", "#3B82F6", "#22D3EE", "#34D399", "#FFFFFF"];

// ميزانية تقريبية (كيلوبايت مضغوط) للتأثيرات قبل ما يقترب الملصق من حد تيليجرام 64KB
const BUDGET_WARN = 30;
const BUDGET_MAX = 42;

export function EffectTile({ type, size = 44 }: { type: EffectType; size?: number }) {
  const m = EFFECT_META[type];
  return (
    <span
      className="shrink-0 rounded-2xl flex items-center justify-center text-white"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(145deg, ${m.grad[0]}, ${m.grad[1]})`,
        boxShadow: `0 8px 18px -8px ${m.grad[1]}, inset 0 1px 0 rgba(255,255,255,.35)`,
      }}
    >
      <m.Icon size={size * 0.46} strokeWidth={2.2} className={`fx-anim fx-anim-${m.anim}`} />
    </span>
  );
}

export function EffectsStudio() {
  const { design, activeEffect, activeLayerKind, setActiveEffectId, addEffect, removeEffect, updateActiveEffect } = useDesign();
  const [sheet, setSheet] = useState(false);
  if (!design) return null;
  const { effects } = design;
  const full = effects.length >= MAX_EFFECT_ITEMS;
  const load = effects.reduce((a, e) => a + EFFECT_META[e.effect_type].weight * Math.pow(e.intensity ?? 1, 0.85), 0);
  const loadPct = Math.min(100, (load / BUDGET_MAX) * 100);
  const loadColor = load >= BUDGET_MAX ? "#EF4444" : load >= BUDGET_WARN ? "#F59E0B" : "#34D399";
  const cur = activeLayerKind === "effect" ? activeEffect : null;
  const rnd = () => Math.floor(Math.random() * 999000) + 1;

  const add = (type: EffectType) => {
    addEffect(type);
    setSheet(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">التأثيرات الحركية</div>
        <div className="text-xs text-tg-hint tabular-nums" dir="ltr">
          {effects.length} / {MAX_EFFECT_ITEMS}
        </div>
      </div>

      {/* الطبقات المضافة */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
        {effects.map((e) => {
          const active = cur?.id === e.id;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => setActiveEffectId(e.id)}
              className="shrink-0 flex items-center gap-2 rounded-2xl ps-1.5 pe-3 py-1.5 text-xs border transition-colors"
              style={{
                background: active ? "rgba(59,130,246,.18)" : "rgba(255,255,255,.05)",
                borderColor: active ? "rgba(59,130,246,.75)" : "rgba(255,255,255,.1)",
              }}
            >
              <EffectTile type={e.effect_type} size={30} />
              <span className="font-medium">{EFFECT_LABELS[e.effect_type]}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setSheet(true)}
          disabled={full}
          className="shrink-0 flex items-center gap-2 rounded-2xl px-3 py-1.5 text-xs border border-dashed text-tg-link disabled:opacity-40"
          style={{ borderColor: "rgba(59,130,246,.55)", background: "rgba(59,130,246,.07)" }}
        >
          <span className="w-[30px] h-[30px] rounded-xl flex items-center justify-center" style={{ background: "rgba(59,130,246,.2)" }}>
            <Plus size={16} strokeWidth={2.6} />
          </span>
          {full ? "وصلت للحد" : "إضافة تأثير"}
        </button>
      </div>

      {effects.length === 0 && (
        <div className="rounded-2xl p-4 text-center" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)" }}>
          <div className="text-sm font-medium mb-1">ما أضفت أي تأثير</div>
          <div className="text-xs text-tg-hint mb-3">15 تأثير جاهز، تقدر تضيف لحد 6 بنفس الملصق وتضبط لون وكثافة وسرعة كل واحد.</div>
          <div className="flex justify-center gap-2">
            {(["fireworks", "sparkles", "hearts", "snow"] as EffectType[]).map((t) => (
              <button key={t} type="button" onClick={() => add(t)} className="flex flex-col items-center gap-1 text-[10px] text-tg-hint">
                <EffectTile type={t} size={42} />
                {EFFECT_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* إعدادات التأثير المحدد */}
      {cur && (
        <div className="rounded-2xl p-3.5 flex flex-col gap-3.5" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)" }}>
          <div className="flex items-center gap-2.5">
            <EffectTile type={cur.effect_type} size={38} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">{EFFECT_LABELS[cur.effect_type]}</div>
              <div className="text-[11px] text-tg-hint">{EFFECT_META[cur.effect_type].hint}</div>
            </div>
            <button
              type="button"
              aria-label="نسخ التأثير"
              disabled={full}
              onClick={() =>
                addEffect(cur.effect_type, {
                  color: cur.color,
                  intensity: cur.intensity,
                  speed: cur.speed,
                  size: cur.size,
                  seed: rnd(),
                  position: { ...cur.position, x: cur.position.x + 26, y: cur.position.y + 22 },
                })
              }
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/8 disabled:opacity-35"
              style={{ background: "rgba(255,255,255,.07)" }}
            >
              <Copy size={16} />
            </button>
            <button
              type="button"
              aria-label="حذف التأثير"
              onClick={() => removeEffect(cur.id)}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-red-300"
              style={{ background: "rgba(239,68,68,.14)" }}
            >
              <Trash2 size={16} />
            </button>
          </div>

          <div>
            <div className="text-xs text-tg-hint mb-2">اللون</div>
            <div className="flex items-center gap-2 flex-wrap">
              {PALETTE.map((c) => {
                const on = cur.color.toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={c}
                    onClick={() => updateActiveEffect({ color: c })}
                    className="w-8 h-8 rounded-full transition-transform active:scale-90"
                    style={{
                      background: c,
                      boxShadow: on ? "0 0 0 2px #1b1f27, 0 0 0 4px #3B82F6" : "inset 0 0 0 1px rgba(255,255,255,.2)",
                    }}
                  />
                );
              })}
              <div className="w-8 h-8 shrink-0 flex items-center justify-center [&>*]:!w-8 [&>*]:!h-8 [&>*]:!rounded-full">
                <ColorSwatchButton value={cur.color} onChange={(hex) => updateActiveEffect({ color: hex })} title="لون التأثير" />
              </div>
            </div>
          </div>

          <SliderControl label="الكثافة (عدد الجزيئات)" value={cur.intensity ?? 1} min={0.4} max={2.5} step={0.1} unit="×" onChange={(v) => updateActiveEffect({ intensity: v })} />
          <SliderControl label="السرعة" value={cur.speed ?? 1} min={0.4} max={2.5} step={0.1} unit="×" onChange={(v) => updateActiveEffect({ speed: v })} />
          <SliderControl label="حجم الجزيئات" value={cur.size ?? 1} min={0.5} max={2} step={0.1} unit="×" onChange={(v) => updateActiveEffect({ size: v })} />

          <button
            type="button"
            onClick={() => updateActiveEffect({ seed: rnd() })}
            className="h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
            style={{ background: "rgba(59,130,246,.16)", border: "1px solid rgba(59,130,246,.45)" }}
          >
            <Shuffle size={16} />
            توزيع جديد
          </button>
          <div className="text-[11px] text-tg-hint leading-relaxed">
            اسحب التأثير على الملصق لتحريكه، كبّره بالزوايا، أو غيّر مدة ظهوره من شريط الملصق تحت.
          </div>
        </div>
      )}

      {/* ثقل التأثيرات على حد تيليجرام */}
      {effects.length > 0 && (
        <div>
          <div className="flex items-center justify-between text-[11px] text-tg-hint mb-1.5">
            <span>ثقل التأثيرات على الملصق</span>
            <span style={{ color: loadColor }}>{load >= BUDGET_MAX ? "قريب من حد تيليجرام" : load >= BUDGET_WARN ? "ثقيل شوية" : "خفيف"}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,.08)" }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${loadPct}%`, background: loadColor }} />
          </div>
          {load >= BUDGET_WARN && (
            <div className="text-[11px] mt-1.5" style={{ color: loadColor }}>
              إذا طلع خطأ "الملصق كبير"، قلّل الكثافة أو احذف تأثير.
            </div>
          )}
        </div>
      )}

      <BottomSheet open={sheet} onClose={() => setSheet(false)}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-base font-bold">مكتبة التأثيرات</div>
            <div className="text-xs text-tg-hint">اختر تأثير يتضاف فوق الملصق (لحد {MAX_EFFECT_ITEMS})</div>
          </div>
          <button type="button" onClick={() => setSheet(false)} aria-label="إغلاق" className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,.08)" }}>
            <X size={18} />
          </button>
        </div>
        {CATS.map((cat) => (
          <div key={cat.id} className="mb-4">
            <div className="text-xs text-tg-hint mb-2 flex items-center gap-1.5">
              <Sparkles size={12} />
              {cat.title}
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {(Object.keys(EFFECT_META) as EffectType[])
                .filter((t) => EFFECT_META[t].cat === cat.id)
                .map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => add(t)}
                    disabled={full}
                    className="rounded-2xl p-2.5 flex flex-col items-center gap-2 text-center active:scale-95 transition-transform disabled:opacity-40"
                    style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.09)" }}
                  >
                    <EffectTile type={t} size={52} />
                    <span className="text-xs font-semibold leading-tight">{EFFECT_LABELS[t]}</span>
                    <span className="text-[10px] text-tg-hint leading-tight min-h-[24px]">{EFFECT_META[t].hint}</span>
                  </button>
                ))}
            </div>
          </div>
        ))}
        {full && <div className="text-xs text-amber-300 text-center pb-2">وصلت للحد الأقصى ({MAX_EFFECT_ITEMS}). احذف تأثير حتى تضيف غيره.</div>}
      </BottomSheet>
    </div>
  );
}
